import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityToolbar } from './components/AccessibilityToolbar';
import { PWAInstallBanner } from './components/PWAInstallBanner';
import { SuperAdminDashboard } from './components/SuperAdminDashboard';
import { TenantAdminDashboard } from './components/TenantAdminDashboard';
import { StudentPortalDashboard } from './components/StudentPortalDashboard';
import { DownloadAppsPage } from './components/DownloadAppsPage';
import { AACBoard } from './components/AACBoard';
import { ExecutiveDashboard } from './components/ExecutiveDashboard';
import { SaaSLandingPage } from './components/SaaSLandingPage';
import { MethodologyPage } from './components/MethodologyPage';
import { AboutUsPage } from './components/AboutUsPage';
import { TheFeedPage } from './components/TheFeedPage';
import { RequestDemoPage } from './components/RequestDemoPage';
import { AuthModal } from './components/AuthModal';
import { ModuleExplorer } from './components/ModuleExplorer';
import { DisabilitySuiteView } from './components/DisabilitySuiteView';
import { MobileAppNavigation } from './components/MobileAppNavigation';
import { FounderDashboard } from './components/FounderDashboard';
import { FounderLoginPage } from './components/FounderLoginPage';
import { FounderPinGate } from './components/FounderPinGate';
import { PricingPage } from './components/PricingPage';
import { MadhyaPradeshPresentation } from './components/MadhyaPradeshPresentation';
import { AbilityBrandLogo, ClarvosCTAButton } from './components/ClarvosBrand';
import { GlobalFooter } from './components/GlobalFooter';
import { audioEngine } from './utils/audioEngine';
import { 
  Shield, Volume2, TrendingUp, LogIn, LogOut, User, Globe, Sparkles, Grid, 
  Users, Smartphone, Download, GraduationCap, Music, Lock, Key, Crown, 
  Menu, X, ArrowUpRight, ShieldCheck, Activity, HeartHandshake
} from 'lucide-react';

export interface UserSession {
  name: string;
  email: string;
  role: string;
  tenant: string;
  subscriptionActive?: boolean;
  enabledModules?: string[];
}

export type ActiveTabType = 
  | 'landing' 
  | 'pricing'
  | 'madhya_pradesh'
  | 'methodology' 
  | 'about_us' 
  | 'the_feed' 
  | 'request_demo' 
  | 'disabilities'
  | 'modules' 
  | 'admin' 
  | 'school_admin' 
  | 'student_portal' 
  | 'aac' 
  | 'analytics' 
  | 'downloads' 
  | 'founder';

export const AppShell: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<ActiveTabType>('landing');
  const [isFounderUnlocked, setIsFounderUnlocked] = useState(false);
  const [activeModuleId, setActiveModuleId] = useState('aac');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserSession | null>(null);
  const [isGlobalMusicPlaying, setIsGlobalMusicPlaying] = useState(false);
  
  // Page-Specific Workspace routing state
  const [activeDisabilityClusterId, setActiveDisabilityClusterId] = useState<string>('autism-neuro');
  const [activeStudentParam, setActiveStudentParam] = useState<string | null>(null);
  const [activeDepartmentParam, setActiveDepartmentParam] = useState<string | null>(null);

  // Navigation scroll hide/show state
  const [isNavHidden, setIsNavHidden] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check URL pathname or hash on mount and listen to navigation
  useEffect(() => {
    const checkRoute = () => {
      const path = window.location.pathname.toLowerCase();
      const hash = window.location.hash.toLowerCase();
      const searchParams = new URLSearchParams(window.location.search);

      // Parse cluster, student, or department query parameters
      const clusterQuery = searchParams.get('cluster');
      if (clusterQuery) setActiveDisabilityClusterId(clusterQuery);

      const studentQuery = searchParams.get('student');
      if (studentQuery) setActiveStudentParam(studentQuery);

      const deptQuery = searchParams.get('department') || searchParams.get('wing');
      if (deptQuery) setActiveDepartmentParam(deptQuery);

      const langQuery = searchParams.get('lang');
      if (langQuery && (langQuery === 'hi' || langQuery === 'en' || langQuery === 'mr')) {
        i18n.changeLanguage(langQuery);
      }

      const moduleQuery = searchParams.get('module');
      if (moduleQuery) {
        setActiveTab('sandbox');
        setActiveModuleId(moduleQuery);
      }

      const clusters = ['autism-neuro', 'speech-language', 'locomotor-physical', 'neurological-stroke', 'visual-blindness', 'hearing-deaf', 'learning-cognitive', 'transition-vocational'];
      for (const c of clusters) {
        if (path.includes(c) || hash.includes(c)) {
          setActiveDisabilityClusterId(c);
          break;
        }
      }
      
      if (path.includes('madhya') || path.includes('presentation-mp') || hash.includes('madhya') || hash.includes('presentation-mp') || path.includes('mp-presentation')) {
        setActiveTab('madhya_pradesh');
      } else if (path.includes('pricing') || hash.includes('pricing')) {
        setActiveTab('pricing');
      } else if (path.includes('founder') || hash.includes('founder')) {
        setActiveTab('founder');
        const isVerified = sessionStorage.getItem('abilityos_founder_pin_verified') === 'true';
        if (isVerified) {
          setIsFounderUnlocked(true);
        }
      } else if (path.includes('methodology') || hash.includes('methodology')) {
        setActiveTab('methodology');
      } else if (path.includes('about') || hash.includes('about')) {
        setActiveTab('about_us');
      } else if (path.includes('feed') || hash.includes('feed')) {
        setActiveTab('the_feed');
      } else if (path.includes('demo') || hash.includes('demo')) {
        setActiveTab('request_demo');
      } else if (path.includes('disabilit') || hash.includes('disabilit')) {
        setActiveTab('disabilities');
      } else if (path.includes('modules') || hash.includes('modules')) {
        setActiveTab('modules');
      } else if (path.includes('downloads') || hash.includes('downloads')) {
        setActiveTab('downloads');
      } else if (path.includes('student') || hash.includes('student')) {
        setActiveTab('student_portal');
      } else if (path.includes('admin') || hash.includes('admin') || path.includes('school') || hash.includes('school')) {
        setActiveTab('school_admin');
      }
    };

    checkRoute();
    window.addEventListener('popstate', checkRoute);
    window.addEventListener('hashchange', checkRoute);
    return () => {
      window.removeEventListener('popstate', checkRoute);
      window.removeEventListener('hashchange', checkRoute);
    };
  }, []);

  // Scroll listener for smooth hide-on-scroll down, show-on-scroll up
  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 40);

      if (currentScrollY > 100 && currentScrollY > lastScrollY + 5) {
        setIsNavHidden(true);
      } else if (currentScrollY < lastScrollY - 5 || currentScrollY < 100) {
        setIsNavHidden(false);
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigateToTab = (tab: ActiveTabType) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const routeMap: Record<ActiveTabType, string> = {
      landing: '/',
      pricing: '/pricing',
      madhya_pradesh: '/madhya-pradesh',
      methodology: '/methodology',
      about_us: '/about-us',
      the_feed: '/the-feed',
      request_demo: '/request-a-demo',
      disabilities: '/disabilities',
      modules: '/modules',
      downloads: '/downloads',
      founder: '/founder',
      admin: '/admin',
      school_admin: '/school-admin',
      student_portal: '/student',
      aac: '/aac',
      analytics: '/analytics',
    };

    window.history.pushState({}, '', routeMap[tab] || '/');
  };

  const navigateToDisabilityWorkspace = (clusterId: string) => {
    setActiveDisabilityClusterId(clusterId);
    setActiveTab('disabilities');
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.history.pushState({}, '', `/disabilities?cluster=${clusterId}`);
  };

  const navigateToStudentWorkspace = (studentId?: string, clusterId?: string) => {
    if (studentId) setActiveStudentParam(studentId);
    if (clusterId) setActiveDisabilityClusterId(clusterId);
    setActiveTab('student_portal');
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const query = studentId ? `?student=${studentId}` : (clusterId ? `?cluster=${clusterId}` : '');
    window.history.pushState({}, '', `/student${query}`);
  };

  const navigateToAdminWingWorkspace = (wingId?: string) => {
    if (wingId) setActiveDepartmentParam(wingId);
    setActiveTab('school_admin');
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const query = wingId ? `?department=${wingId}` : '';
    window.history.pushState({}, '', `/school-admin${query}`);
  };

  const handleToggleGlobalMusic = () => {
    const newState = audioEngine.toggleGlobalBackgroundMusic();
    setIsGlobalMusicPlaying(newState);
  };

  const handleLoginSuccess = (user: UserSession) => {
    setCurrentUser(user);
    if (user.role === 'FOUNDER') {
      navigateToTab('founder');
    } else if (user.role === 'SUPER_ADMIN') {
      navigateToTab('admin');
    } else if (user.role === 'STUDENT') {
      navigateToTab('student_portal');
    } else {
      navigateToTab('school_admin');
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    navigateToTab('landing');
  };

  const handleLaunchModule = (moduleId: string) => {
    if (!currentUser) {
      setIsAuthModalOpen(true);
      return;
    }
    setActiveModuleId(moduleId);
    navigateToTab('modules');
  };

  return (
    <div className="min-h-screen bg-clarvos-cream flex flex-col font-sans text-clarvos-black selection:bg-clarvos-yellow selection:text-clarvos-black">
      
      <PWAInstallBanner />

      {/* Accessible Skip Link */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 bg-clarvos-yellow text-clarvos-black font-extrabold px-4 py-2 z-50 rounded-full shadow-lg border border-black"
      >
        Skip to main content
      </a>

      {/* ===================================================================== */}
      {/* CLARVOS FLOATING PILL NAVBAR (Hidden in Full Paged Presentation)        */}
      {/* ===================================================================== */}
      {activeTab !== 'madhya_pradesh' && (
        <div 
          className={`fixed top-0 left-0 right-0 z-50 pt-3 sm:pt-4 px-4 flex justify-center pointer-events-none transition-transform duration-500 ease-clarvos ${
            isNavHidden ? '-translate-y-28' : 'translate-y-0'
          }`}
        >
        <header className="pointer-events-auto bg-white/95 backdrop-blur-md rounded-full px-5 sm:px-7 py-2.5 sm:py-3 border border-black/10 shadow-lg flex items-center justify-between gap-4 sm:gap-6 max-w-5xl w-full">
          
          {/* Left: Brand Wordmark */}
          <div 
            className="cursor-pointer group flex items-center shrink-0" 
            onClick={() => navigateToTab('landing')}
          >
            <AbilityBrandLogo variant="dark" size="sm" />
          </div>

          {/* Center Links (Desktop - Canonical Clarvos Nav) */}
          <nav className="hidden lg:flex items-center gap-7 text-[13px] font-semibold text-neutral-800">
            <button 
              onClick={() => navigateToTab('methodology')} 
              className={`hover:text-black transition-colors relative py-1 cursor-pointer ${
                activeTab === 'methodology' ? 'text-black font-extrabold border-b-2 border-[#ffd64e]' : ''
              }`}
            >
              Methodology
            </button>
            <button 
              onClick={() => {
                if (activeTab !== 'landing') {
                  navigateToTab('landing');
                }
                setTimeout(() => {
                  const el = document.getElementById('comparison');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }} 
              className="hover:text-black transition-colors relative py-1 cursor-pointer flex items-center gap-1 text-slate-600 font-medium hover:font-bold"
            >
              <span>Comparison</span>
            </button>
            <button 
              onClick={() => navigateToTab('pricing')} 
              className={`hover:text-black transition-colors relative py-1 cursor-pointer font-bold ${
                activeTab === 'pricing' ? 'text-black font-extrabold border-b-2 border-[#ffd64e]' : 'text-slate-700'
              }`}
            >
              Pricing
            </button>
            <button 
              onClick={() => navigateToTab('disabilities')} 
              className={`hover:text-black transition-colors relative py-1 cursor-pointer flex items-center gap-1.5 ${
                activeTab === 'disabilities' ? 'text-black font-extrabold border-b-2 border-[#ffd64e]' : ''
              }`}
            >
              <Sparkles className="w-3.5 h-3.5 text-[#ffd64e]" />
              <span>Disability Suites</span>
            </button>
            <button 
              onClick={() => navigateToTab('downloads')} 
              className={`hover:text-black transition-colors relative py-1 cursor-pointer ${
                activeTab === 'downloads' ? 'text-black font-extrabold border-b-2 border-[#ffd64e]' : ''
              }`}
            >
              Downloads
            </button>
            <button 
              onClick={() => navigateToTab('about_us')} 
              className={`hover:text-black transition-colors relative py-1 cursor-pointer ${
                activeTab === 'about_us' ? 'text-black font-extrabold border-b-2 border-[#ffd64e]' : ''
              }`}
            >
              About Us
            </button>
            <button 
              onClick={() => navigateToTab('the_feed')} 
              className={`hover:text-black transition-colors relative py-1 cursor-pointer ${
                activeTab === 'the_feed' ? 'text-black font-extrabold border-b-2 border-[#ffd64e]' : ''
              }`}
            >
              The Feed
            </button>
          </nav>

          {/* Right Action Cluster */}
          <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
            {/* Global Music Audio Toggle */}
            <button
              onClick={handleToggleGlobalMusic}
              title="Toggle Bio-Rhythm Ambient Audio"
              className={`w-9 h-9 rounded-full border transition-all flex items-center justify-center cursor-pointer ${
                isGlobalMusicPlaying 
                  ? 'bg-[#ffd64e] text-[#1c1b1f] border-[#ffd64e] shadow-sm' 
                  : 'bg-[#f9f6f0] text-[#1c1b1f] border-black/10 hover:border-black/30'
              }`}
            >
              <Music className="w-3.5 h-3.5" />
            </button>

            {/* Talk to Us Button (White Pill with Outline) */}
            <button
              onClick={() => navigateToTab('request_demo')}
              className="hidden sm:inline-flex items-center px-4 py-2 rounded-full bg-white hover:bg-neutral-50 text-[#1c1b1f] border border-black/15 font-bold text-xs uppercase tracking-wider transition-all shadow-xs cursor-pointer"
            >
              <span>Talk to Us</span>
            </button>

            {/* Request a Demo Button (Clarvos Solar Yellow Pill) */}
            <button
              onClick={() => navigateToTab('request_demo')}
              className="inline-flex items-center gap-1.5 px-4 sm:px-5 py-2 rounded-full bg-[#ffd64e] hover:bg-[#f5cb3a] text-[#1c1b1f] font-black text-xs uppercase tracking-wider transition-all shadow-sm cursor-pointer"
            >
              <span>Request a Demo</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>

            {/* Auth / Profile Pill */}
            {currentUser ? (
              <div className="flex items-center gap-2 pl-1">
                <button 
                  onClick={() => {
                    if (currentUser.role === 'FOUNDER') navigateToTab('founder');
                    else if (currentUser.role === 'SUPER_ADMIN') navigateToTab('admin');
                    else if (currentUser.role === 'STUDENT') navigateToTab('student_portal');
                    else navigateToTab('school_admin');
                  }}
                  className="w-8 h-8 rounded-full bg-[#1c1b1f] text-[#ffd64e] flex items-center justify-center font-bold text-xs cursor-pointer shadow border border-[#ffd64e]/40"
                  title={`${currentUser.name} (${currentUser.role})`}
                >
                  {currentUser.name.charAt(0)}
                </button>
                <button
                  onClick={handleLogout}
                  className="p-1.5 rounded-full text-slate-500 hover:text-rose-600 transition-colors cursor-pointer"
                  title="Sign Out"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="hidden xl:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#1c1b1f] hover:bg-[#222222] text-white text-xs font-bold uppercase tracking-wider transition-all shadow-sm cursor-pointer"
              >
                <LogIn className="w-3.5 h-3.5 text-[#ffd64e]" />
                <span>Sign In</span>
              </button>
            )}

            {/* Mobile Menu Hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-full bg-[#f9f6f0] border border-black/10 text-[#1c1b1f] cursor-pointer"
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>

        </header>
      </div>
      )}

      {/* Mobile Menu Dropdown Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-clarvos-black/90 backdrop-blur-lg pt-24 px-6 flex flex-col justify-between pb-12 animate-fade-in">
          <div className="space-y-4 text-center">
            {[
              { label: 'Pricing', tab: 'pricing' },
              { label: 'Madhya Pradesh Proposal', tab: 'madhya_pradesh' },
              { label: 'Methodology', tab: 'methodology' },
              { label: 'Disability Suites', tab: 'disabilities' },
              { label: 'About Us', tab: 'about_us' },
              { label: 'The Feed', tab: 'the_feed' },
              { label: 'Downloads', tab: 'downloads' },
              { label: 'Request a Demo', tab: 'request_demo' },
            ].map((item) => (
              <button
                key={item.tab}
                onClick={() => navigateToTab(item.tab as ActiveTabType)}
                className="block w-full py-3 text-xl font-extrabold text-white hover:text-clarvos-yellow uppercase tracking-wider transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="pt-8 border-t border-white/20 text-center space-y-3">
            {!currentUser ? (
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  setIsAuthModalOpen(true);
                }}
                className="w-full py-3.5 rounded-full bg-clarvos-yellow text-clarvos-black font-extrabold text-sm uppercase tracking-wider"
              >
                Sign In to AbilityOS
              </button>
            ) : (
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="w-full py-3.5 rounded-full bg-rose-600 text-white font-extrabold text-sm uppercase tracking-wider"
              >
                Sign Out ({currentUser.name})
              </button>
            )}
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* MAIN VIEW CONTENT AREA                                                */}
      {/* ===================================================================== */}
      <main id="main-content" className="flex-1">
        
        {activeTab === 'landing' && (
          <SaaSLandingPage 
            onOpenAuth={() => setIsAuthModalOpen(true)}
            onExploreDemo={(tab) => {
              if (tab === 'founder') {
                navigateToTab('founder');
                return;
              }
              if (!currentUser) {
                setIsAuthModalOpen(true);
                return;
              }
              if (tab === 'admin' || tab === 'aac' || tab === 'analytics' || tab === 'downloads' || tab === 'school_admin' || tab === 'student_portal') {
                navigateToTab(tab as any);
              } else {
                handleLaunchModule(tab);
              }
            }}
            onSelectModule={handleLaunchModule}
            onRequestDemo={() => navigateToTab('request_demo')}
          />
        )}

        {activeTab === 'methodology' && (
          <MethodologyPage 
            onExploreDemo={navigateToTab}
            onRequestDemo={() => navigateToTab('request_demo')}
          />
        )}

        {activeTab === 'about_us' && (
          <AboutUsPage 
            onRequestDemo={() => navigateToTab('request_demo')}
            onExploreDemo={navigateToTab}
          />
        )}

        {activeTab === 'the_feed' && (
          <TheFeedPage 
            onRequestDemo={() => navigateToTab('request_demo')}
          />
        )}

        {activeTab === 'request_demo' && (
          <RequestDemoPage 
            onBackToHome={() => navigateToTab('landing')}
          />
        )}

        {activeTab === 'madhya_pradesh' && (
          <MadhyaPradeshPresentation
            onNavigateToTab={navigateToTab}
            onSelectModule={handleLaunchModule}
            onBackToFounder={() => navigateToTab('founder')}
          />
        )}

        {activeTab === 'disabilities' && (
          <DisabilitySuiteView 
            initialDisabilityId={activeDisabilityClusterId}
            currentUser={currentUser}
            onLaunchModule={handleLaunchModule}
            onOpenAAC={() => navigateToTab('aac')}
            onOpenDownloads={() => navigateToTab('downloads')}
            onNavigateToStudentWorkspace={navigateToStudentWorkspace}
            onNavigateToAdminWing={navigateToAdminWingWorkspace}
          />
        )}

        {activeTab === 'modules' && (
          <DisabilitySuiteView 
            initialDisabilityId={activeDisabilityClusterId}
            currentUser={currentUser}
            onLaunchModule={handleLaunchModule}
            onOpenAAC={() => navigateToTab('aac')}
            onOpenDownloads={() => navigateToTab('downloads')}
            onNavigateToStudentWorkspace={navigateToStudentWorkspace}
            onNavigateToAdminWing={navigateToAdminWingWorkspace}
          />
        )}

        {activeTab === 'downloads' && (
          <DownloadAppsPage 
            onLaunchModule={(modId) => {
              setActiveTab('sandbox');
              setActiveModuleId(modId);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        )}

        {activeTab === 'admin' && (
          currentUser?.role === 'SUPER_ADMIN' ? (
            <SuperAdminDashboard />
          ) : (
            <div className="max-w-xl mx-auto py-32 px-6 text-center space-y-4">
              <div className="w-16 h-16 bg-white border border-clarvos-gray-300 rounded-3xl flex items-center justify-center mx-auto shadow-md">
                <Shield className="w-8 h-8 text-clarvos-black" />
              </div>
              <h3 className="text-3xl font-extrabold text-clarvos-black">Super Admin Access Required</h3>
              <p className="text-sm text-clarvos-gray-600">Please sign in with a Super Admin account to access system telemetry.</p>
              <ClarvosCTAButton
                text="Sign In as Super Admin"
                onClick={() => setIsAuthModalOpen(true)}
                variant="yellow"
                size="md"
              />
            </div>
          )
        )}

        {activeTab === 'school_admin' && (
          <TenantAdminDashboard 
            isSubscriptionActive={currentUser?.subscriptionActive !== false} 
            currentUser={currentUser || {
              name: 'Dr. Evelyn Reed (Clinical Director)',
              email: 'director.reed@abilityos.org',
              role: 'TENANT_ADMIN',
              tenant: 'AbilityOS Special Academy',
              subscriptionActive: true
            }} 
            initialDepartmentId={activeDepartmentParam}
            onNavigateToDisabilitySuite={navigateToDisabilityWorkspace}
          />
        )}

        {activeTab === 'student_portal' && (
          <StudentPortalDashboard 
            currentUser={currentUser || {
              name: 'Rahul Sharma (Guest Student)',
              email: 'student.rahul@abilityos.org',
              role: 'STUDENT',
              tenant: 'AbilityOS Special Academy',
              subscriptionActive: true
            }} 
            initialStudentId={activeStudentParam}
            onOpenAAC={() => navigateToTab('aac')} 
            onSelectModule={handleLaunchModule}
            onNavigateToDisabilitySuite={navigateToDisabilityWorkspace}
            onNavigateToAdminWing={navigateToAdminWingWorkspace}
          />
        )}

        {activeTab === 'aac' && (
          <div className="pt-28 pb-16 px-4 max-w-5xl mx-auto">
            <AACBoard />
          </div>
        )}

        {activeTab === 'analytics' && (
          currentUser ? (
            <ExecutiveDashboard />
          ) : (
            <div className="max-w-xl mx-auto py-32 px-6 text-center space-y-4">
              <div className="w-16 h-16 bg-white border border-clarvos-gray-300 rounded-3xl flex items-center justify-center mx-auto shadow-md">
                <TrendingUp className="w-8 h-8 text-clarvos-black" />
              </div>
              <h3 className="text-3xl font-extrabold text-clarvos-black">Analytics BI Sign In Required</h3>
              <p className="text-sm text-clarvos-gray-600">Please sign in to view executive progress analytics and clinical telemetry.</p>
              <ClarvosCTAButton
                text="Sign In"
                onClick={() => setIsAuthModalOpen(true)}
                variant="yellow"
                size="md"
              />
            </div>
          )
        )}

        {activeTab === 'founder' && (
          !isFounderUnlocked ? (
            <FounderPinGate 
              onUnlock={() => setIsFounderUnlocked(true)}
              onCancel={() => navigateToTab('landing')}
            />
          ) : currentUser?.role === 'FOUNDER' ? (
            <FounderDashboard 
              currentUser={currentUser}
              onImpersonateLogin={(user) => {
                handleLoginSuccess(user);
              }}
              onReturnToGateway={() => {
                setCurrentUser(null);
              }}
              onLockOut={() => {
                sessionStorage.removeItem('abilityos_founder_pin_verified');
                setIsFounderUnlocked(false);
                setCurrentUser(null);
                navigateToTab('landing');
              }}
              onNavigateToTab={navigateToTab}
              onLaunchModule={handleLaunchModule}
            />
          ) : (
            <FounderLoginPage 
              onLogin={(user) => {
                handleLoginSuccess(user);
              }}
              onLockOut={() => {
                sessionStorage.removeItem('abilityos_founder_pin_verified');
                setIsFounderUnlocked(false);
                setCurrentUser(null);
                navigateToTab('landing');
              }}
              onNavigateToTab={navigateToTab}
            />
          )
        )}

        {activeTab === 'pricing' && (
          <PricingPage 
            onSelectPlan={(tierId, cadence, curr) => {
              setIsAuthModalOpen(true);
            }}
            onRequestDistrictDemo={() => navigateToTab('request_demo')}
            onNavigateToTab={navigateToTab}
          />
        )}

      </main>

      {/* ===================================================================== */}
      {/* 4-PILLAR ARCHITECTURAL GLOBAL FOOTER (Hidden in Full Paged Presentation)*/}
      {/* ===================================================================== */}
      {activeTab !== 'madhya_pradesh' && (
        <GlobalFooter 
          onNavigateToTab={navigateToTab}
          onSelectModule={handleLaunchModule}
          onSelectDisability={navigateToDisabilityWorkspace}
          onScrollToComparison={() => {
            if (activeTab !== 'landing') {
              navigateToTab('landing');
            }
            setTimeout(() => {
              const el = document.getElementById('comparison');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }, 150);
          }}
        />
      )}

      {/* Mobile Smartphone Bottom Tab Navigation Bar */}
      {activeTab !== 'madhya_pradesh' && (
        <MobileAppNavigation 
          activeTab={activeTab}
          onTabChange={(tab) => {
            if (!currentUser && (tab === 'modules' || tab === 'school_admin' || tab === 'analytics' || tab === 'student_portal')) {
              setIsAuthModalOpen(true);
              return;
            }
            navigateToTab(tab as ActiveTabType);
          }}
          currentUser={currentUser}
        />
      )}

      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Floating Accessibility Toolbar */}
      <AccessibilityToolbar />
    </div>
  );
};
